import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Logo } from "../../assets/svg";
import TextInput from "../../components/inputs/TextInput";
import DateInput from "../../components/dateinput/dateinput";
import Dropdown, { DropdownTypable } from "../../components/dropdown/dropdown";
import Checkbox from "../../components/checkbox/checkbox";
import ReactDOM from "react-dom";
import COTPInput from "../../components/inputs/OTPInput";
import {
  useAddEarlyAccessUserMutation,
  useIP,
  usePostEarlyAccessResendOtpMutation,
  usePostEarlyAccessVerifyOtpMutation,
  usePrimarySportDetails,
} from "../../api/earlyaccess/queryHooks";
import { getDeviceDetails } from "../../api/utils";
import { EARLY_ACCESS_SECURE_KEY } from "../../api/consts";
import Countrycodepicker from "../../components/dropdown/countrycodepicker";
import EarlyAccessSuccessfullRegistration from "./EarlyAccessSuccessfullRegistration";
import Fullscreenloader from "../../components/loader/fullscreenloader";

const ResendOTPButton = ({ data, phonecode }) => {
  const TIMEOUT = 179;
  const [timer, setTimer] = useState(TIMEOUT);

  const { mutate, isLoading, isError } = usePostEarlyAccessResendOtpMutation();

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? `0${secs}` : secs}`;
  };

  const handleClick = () => {
    mutate({
      phone: `${phonecode}${data.phoneNumber}`,
      secure_key: EARLY_ACCESS_SECURE_KEY,
    });
    setTimer(TIMEOUT);
  };

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  return (
    <button
      disabled={isError || isLoading || timer > 0}
      className="text-[#AAAAAA] text-xs disabled:opacity-50"
      onClick={handleClick}
    >
      {timer > 0 ? `Resend (${formatTime(timer)})s` : "Resend OTP"}
    </button>
  );
};

const VerifyOTPPopup = ({
  data,
  setData,
  phonecode,
  submitForm,
  isLoading,
  onClickChange,
  setAddedUser,
}) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState(null);

  const { mutate, isLoading: verifyingOTP } =
    usePostEarlyAccessVerifyOtpMutation({
      onSuccess: () => {
        setAddedUser(true);
      },
      onError: () => {
        setError("Invalid OTP Entered!");
      },
    });

  const handleSubmit = () => {
    setError(null);
    mutate({
      phone: `${phonecode}${data.phoneNumber}`,
      otp: otp,
    });
  };

  if (data === null) return null;
  return ReactDOM.createPortal(
    <div className="z-20 bg-[#0000009e] fixed top-0 left-0 w-screen h-screen flex items-center justify-center">
      <div className="w-full rounded-3xl bg-[#2C2D36] mx-5 p-10 max-w-[375px] shadow-2xl shadow-[#292929]">
        <h3 className="font-bold text-[22px]">Welcome {data.fullName}!</h3>

        <p className="text-[#AAAAAA] text-xs">
          We have sent you an SMS with access code on <br />
          +91 {data.phoneNumber}{" "}
          <span
            className="text-white underline cursor-pointer"
            onClick={onClickChange}
          >
            Change
          </span>
        </p>
        <div className="flex flex-col items-center justify-center mt-5 gap-4">
          <COTPInput otp={otp} setOtp={setOtp} shouldAutoFocus={true} />
          <button
            onClick={handleSubmit}
            disabled={verifyingOTP || otp?.length !== 6}
            className="text-sm bg-gradient-to-r from-pinkish to-purpleish max-w-fit px-5 py-2 rounded-3xl lg:text-base disabled:opacity-50"
          >
            {verifyingOTP ? "Verifying" : "Verify"}
          </button>
          {error ? <p className="text-red-400 text-xs">{error}</p> : null}
          <ResendOTPButton data={data} phonecode={phonecode} />
        </div>
      </div>
    </div>,
    document.body
  );
};

const Form = ({ setAddedUser }) => {
  const [data, setData] = useState(null);
  const DEFAULT_CODE = "+91";
  const [phonecode, setPhonecode] = useState(DEFAULT_CODE);
  const {
    isLoading: loadingprimaryoptions,
    isError: loadingprimaryoptionserror,
    data: primarysportoptions,
  } = usePrimarySportDetails();

  const {
    data: ipdata,
    isLoading: loadingip,
    isError: loadingiperror,
  } = useIP();

  const { mutate, isLoading } = useAddEarlyAccessUserMutation({
    onSuccess: (data) => {
      if (data?.data?.is_user_exist) setAddedUser(true);
      else setData(formik.values);
      setData(formik.values);
    },
  });

  const submitForm = (values) => {
    const device_details = getDeviceDetails();

    mutate({
      phone: `${phonecode}${values.phoneNumber}`,
      full_name: values.fullName,
      secure_key: EARLY_ACCESS_SECURE_KEY,
      email: values.email,
      gender: values.gender.value,
      primary_sports: values.primarySport.value,
      dob: values.dateOfBirth,
      registration_for: values.registeringfor.value,
      details: values.details,
      level: values.level.value,

      os_type: device_details.osType,
      os_version: device_details.osVersion,
      device_type: device_details.deviceType,
      device_model: device_details.osModel,
      browser: device_details.browser,
      ip_details: ipdata.ip,
    });
  };

  const formik = useFormik({
    initialValues: {
      phoneNumber: "",
      fullName: "",
      email: "",
      gender: null,
      dateOfBirth: null,
      primarySport: null,
      registeringfor: null,
      termsAccepted: false,
      details: "",
      level: null,
    },
    validationSchema: Yup.object({
      phoneNumber: Yup.string()
        .matches(/^\d{10}$/, "Invalid Phone Number")
        .required("Phone number is required"),
      fullName: Yup.string()
        .min(2, "Full name must be at least 2 characters")
        .required("Full name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      gender: Yup.object()
        .shape({
          id: Yup.number().required(),
          value: Yup.string().required(),
          label: Yup.string().required(),
        })
        .nullable()
        .required("Gender is required"),
      dateOfBirth: Yup.date().required("Date of birth is required"),
      primarySport: Yup.object()
        .shape({
          id: Yup.number().required(),
          value: Yup.string().required(),
          label: Yup.string().required(),
        })
        .nullable()
        .required("Primary sport is required"),
      registeringfor: Yup.object()
        .shape({
          id: Yup.number().required(),
          value: Yup.string().required(),
          label: Yup.string().required(),
        })
        .nullable()
        .required("Registering For is required"),
      level: Yup.object()
        .shape({
          id: Yup.number().required(),
          value: Yup.string().required(),
          label: Yup.string().required(),
        })
        .nullable()
        .required("Registering For is required"),
      termsAccepted: Yup.bool()
        .oneOf([true], "You must accept the terms and conditions")
        .required("You must accept the terms and conditions"),
      details: Yup.string().max(500, "Details must be 500 characters or less"),
    }),
    onSubmit: (values) => {
      submitForm(values);
    },
  });

  if (loadingprimaryoptionserror || loadingiperror)
    return <div>Something Went Wrong!</div>;

  if (loadingprimaryoptions || loadingip) {
    return <Fullscreenloader />;
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="bg-black w-full rounded-xl p-5"
    >
      <h3 className="text-xl mb-6 text-center">
        Transform Your Sports Journey with Strikie
      </h3>
      <div className="flex flex-col gap-3">
        <div>
          <div className="flex items-center justify-center">
            <Countrycodepicker
              defaultCode={DEFAULT_CODE}
              onChange={(value) => setPhonecode(value)}
            />
            <div className="flex-grow ml-3">
              <TextInput
                autoComplete="off"
                type="number"
                name="phoneNumber"
                className="w-full hide-number-input-spin"
                placeholderClassName={
                  formik.touched.phoneNumber && formik.errors.phoneNumber
                    ? "text-red-400"
                    : ""
                }
                placeholder="Phone Number"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
          </div>
          {/* {formik.touched.phoneNumber && formik.errors.phoneNumber && (
            <p className="text-red-400 text-xs mt-1">
              {formik.errors.phoneNumber}
            </p>
          )} */}
        </div>

        <div className="border-b border-[#282828]"></div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div>
            <TextInput
              name="fullName"
              autoComplete="off"
              className="w-full"
              placeholder="Full Name"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholderClassName={
                formik.touched.fullName && formik.errors.fullName
                  ? "text-red-400"
                  : ""
              }
            />
            {/* {formik.touched.fullName && formik.errors.fullName && (
              <p className="text-red-400 text-xs mt-1">
                {formik.errors.fullName}
              </p>
            )} */}
          </div>

          <div>
            <TextInput
              name="email"
              autoComplete="off"
              className="w-full"
              placeholder="Email Address"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholderClassName={
                formik.touched.email && formik.errors.email
                  ? "text-red-400"
                  : ""
              }
            />
            {/* {formik.touched.email && formik.errors.email && (
              <p className="text-red-400 text-xs mt-1">{formik.errors.email}</p>
            )} */}
          </div>
        </div>

        <div>
          <Dropdown
            placeholder="Gender"
            value={formik.values.gender}
            onChange={(value) => formik.setFieldValue("gender", value)}
            options={[
              { id: 1, value: "Male", label: "Male" },
              { id: 2, value: "Female", label: "Female" },
              { id: 3, value: "Others", label: "Others" },
            ]}
            placeholderClassName={
              formik.touched.gender && formik.errors.gender
                ? "text-red-400"
                : ""
            }
          />
          {/* {formik.touched.gender && formik.errors.gender && (
            <p className="text-red-400 text-xs mt-1">Gender is required</p>
          )} */}
        </div>

        <div>
          <DateInput
            placeholder="DOB (MM/DD/YYYY)"
            className="lg:w-1/2"
            selected={formik.values.dateOfBirth}
            onChange={(date) => formik.setFieldValue("dateOfBirth", date)}
            maxDate={new Date()}
            pickerClassName={
              formik.touched.dateOfBirth && formik.errors.dateOfBirth
                ? "placeholder-red-400"
                : ""
            }
          />
          {/* {formik.touched.dateOfBirth && formik.errors.dateOfBirth && (
            <p className="text-red-400 text-xs mt-1">
              {formik.errors.dateOfBirth}
            </p>
          )} */}
        </div>

        <div>
          <Dropdown
            placeholder="Registering For"
            value={formik.values.registeringfor}
            onChange={(value) => formik.setFieldValue("registeringfor", value)}
            options={[
              { id: 1, value: "self", label: "Self" },
              { id: 2, value: "son", label: "Son" },
              { id: 3, value: "daughter", label: "Daughter" },
              { id: 4, value: "friend", label: "Friend" },
              { id: 5, value: "familymember", label: "Family Member" },
            ]}
            placeholderClassName={
              formik.touched.registeringfor && formik.errors.registeringfor
                ? "text-red-400"
                : ""
            }
          />
          {/* {formik.touched.registeringfor && formik.errors.registeringfor && (
            <p className="text-red-400 text-xs mt-1">
              Registering For is required
            </p>
          )} */}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div>
            <DropdownTypable
              placeholder="Primary Sports"
              value={formik.values.primarySport}
              onChange={(value) => formik.setFieldValue("primarySport", value)}
              options={primarysportoptions.map((item) => ({
                id: item.id,
                value: item.id,
                label: item.title,
              }))}
              placeholderClassName={
                formik.touched.primarySport && formik.errors.primarySport
                  ? "text-red-400"
                  : ""
              }
            />
            {/* {formik.touched.primarySport && formik.errors.primarySport && (
            <p className="text-red-400 text-xs mt-1">
              Primary Sport is required
            </p>
          )} */}
          </div>

          <div>
            <Dropdown
              placeholder="Level"
              value={formik.values.level}
              onChange={(value) => formik.setFieldValue("level", value)}
              options={[
                { id: 1, value: "Beginner", label: "Beginner" },
                { id: 2, value: "Intermediate", label: "Intermediate" },
                { id: 3, value: "Advanced", label: "Advanced" },
                { id: 4, value: "Professional", label: "Professional" },
                { id: 5, value: "District", label: "District" },
                { id: 6, value: "State", label: "State" },
                { id: 7, value: "National", label: "National" },
              ]}
              placeholderClassName={
                formik.touched.registeringfor && formik.errors.registeringfor
                  ? "text-red-400"
                  : ""
              }
            />
            {/* {formik.touched.registeringfor && formik.errors.registeringfor && (
            <p className="text-red-400 text-xs mt-1">
              Registering For is required
            </p>
          )} */}
          </div>
        </div>

        <div>
          <textarea
            name="details"
            placeholder="Add your School/College/Club/Academy…"
            className="w-full border border-[#444444] placeholder-[#5d5d64] bg-[#2C2D36] rounded-2xl px-2 py-4 text-sm"
            value={formik.values.details}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.details && formik.errors.details && (
            <p className="text-red-400 text-xs mt-1">{formik.errors.details}</p>
          )}
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center">
          <Checkbox
            name="termsAccepted"
            checked={formik.values.termsAccepted}
            onChange={(e) =>
              formik.setFieldValue("termsAccepted", e.target.checked)
            }
          />
          <span className="text-xs text-[#AAAAAA] ml-3">I Agree,</span>
          {formik.touched.termsAccepted && formik.errors.termsAccepted && (
            <span className="text-red-400 text-xs ml-1">
              {formik.errors.termsAccepted}
            </span>
          )}
        </div>

        <div className="mt-2">
          <p className="text-[#555555] text-xs">
            By checking this box, you have read and agree to the{" "}
            <a
              href="https://www.strikie.com/terms-conditions.html"
              target="_blank"
              rel="noreferrer"
              className="text-white text-nowrap"
            >
              Terms & Conditions
            </a>
            , including our{" "}
            <a
              href="https://www.strikie.com/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="text-white text-nowrap"
            >
              Privacy Policy
            </a>
            , and consent to receive communications from us regarding your
            account and our services.
          </p>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          type="submit"
          disabled={isLoading}
          className="text-sm bg-gradient-to-r from-pinkish to-purpleish max-w-fit px-5 py-[10px] rounded-3xl lg:text-base disabled:opacity-50"
        >
          {isLoading ? "Please Wait..." : "Submit"}
        </button>
      </div>

      <VerifyOTPPopup
        data={data}
        setData={setData}
        submitForm={submitForm}
        isLoading={isLoading}
        onClickChange={() => setData(null)}
        phonecode={phonecode}
        setAddedUser={setAddedUser}
      />
    </form>
  );
};

const EarlyAccessPage = () => {
  const [addedUser, setAddedUser] = useState(false);

  useEffect(() => {
    document.body.classList.add("bg-hero-image");
    return () => {
      document.body.classList.remove("bg-hero-image");
    };
  }, []);

  if (addedUser)
    return <EarlyAccessSuccessfullRegistration setAddedUser={setAddedUser} />;

  return (
    <>
      <header className="flex items-center justify-between p-5 max-w-screen-sm mx-auto">
        <Logo />
        <h3 className="text-base font-bold">Get Early Access Pass!</h3>
      </header>

      <div className="px-5 max-w-screen-sm mx-auto mt-2">
        <Form setAddedUser={setAddedUser} />
      </div>
    </>
  );
};

export default EarlyAccessPage;
